import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  addOrderParameters,
  cancelOrder,
  getOrder,
  getOrderReviews,
  listOrderAssignees,
  listOrderCities,
  listOrderCustomers,
  listParameterTemplates,
  raiseOrderTicket,
  requestReplacement,
  updateExpectedDelivery,
  updateOrderStatus,
} from './api';
import type {
  AddParametersInput,
  OrderStatus,
  RaiseTicketInput,
  RequestReplacementInput,
} from './types';

export const orderKeys = {
  all: ['orders'] as const,
  metaCities: () => [...orderKeys.all, 'meta', 'cities'] as const,
  metaCustomers: (search: string) => [...orderKeys.all, 'meta', 'customers', search] as const,
  detail: (id: string) => [...orderKeys.all, 'detail', id] as const,
  reviews: (id: string) => [...orderKeys.all, 'detail', id, 'reviews'] as const,
  assignees: () => [...orderKeys.all, 'meta', 'assignees'] as const,
  parameterTemplates: () => [...orderKeys.all, 'meta', 'parameter-templates'] as const,
};

/** City filter options; cached 5 min (changes rarely). */
export function useOrderCities() {
  return useQuery({
    queryKey: orderKeys.metaCities(),
    queryFn: listOrderCities,
    staleTime: 5 * 60 * 1000,
  });
}

/** Customer typeahead; `search` is the debounced term. Keeps prior results while refetching. */
export function useOrderCustomers(search: string) {
  return useQuery({
    queryKey: orderKeys.metaCustomers(search),
    queryFn: () => listOrderCustomers(search),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
}

// ── Order Detail (feature 102116) ─────────────────────────────────────────────

/** Full order detail. Always refetched on mount (no stale cache — AC: F5 reloads from server). */
export function useOrder(id: string) {
  return useQuery({
    queryKey: orderKeys.detail(id),
    queryFn: () => getOrder(id),
    staleTime: 0,
  });
}

/** Per-device customer reviews; fetched lazily (Reviews drawer). */
export function useOrderReviews(id: string, enabled: boolean) {
  return useQuery({
    queryKey: orderKeys.reviews(id),
    queryFn: () => getOrderReviews(id),
    enabled,
    staleTime: 60 * 1000,
  });
}

/** Assignee options (Raise-a-ticket form). */
export function useOrderAssignees(enabled: boolean) {
  return useQuery({
    queryKey: orderKeys.assignees(),
    queryFn: listOrderAssignees,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Parameter-template options (Add Parameters modal). */
export function useParameterTemplates(enabled: boolean) {
  return useQuery({
    queryKey: orderKeys.parameterTemplates(),
    queryFn: listParameterTemplates,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Status / delivery / cancel / parameters all return the updated order — write it straight
 *  into the detail cache so the page reflects the change without a refetch round-trip. */
export function useUpdateOrderStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: OrderStatus) => updateOrderStatus(id, status),
    onSuccess: (order) => {
      qc.setQueryData(orderKeys.detail(id), order);
      void qc.invalidateQueries({ queryKey: orderKeys.all });
    },
  });
}

export function useUpdateExpectedDelivery(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (expectedDelivery: string) => updateExpectedDelivery(id, expectedDelivery),
    onSuccess: (order) => qc.setQueryData(orderKeys.detail(id), order),
  });
}

export function useCancelOrder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cancelOrder(id),
    onSuccess: (order) => {
      qc.setQueryData(orderKeys.detail(id), order);
      void qc.invalidateQueries({ queryKey: orderKeys.all });
    },
  });
}

export function useAddParameters(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddParametersInput) => addOrderParameters(id, input),
    onSuccess: (order) => qc.setQueryData(orderKeys.detail(id), order),
  });
}

export function useRaiseTicket(id: string) {
  return useMutation({
    mutationFn: (input: RaiseTicketInput) => raiseOrderTicket(id, input),
  });
}

export function useRequestReplacement(id: string) {
  return useMutation({
    mutationFn: (input: RequestReplacementInput) => requestReplacement(id, input),
  });
}
